import { Pressable, View } from "react-native";
import { FlaskConical, Info } from "lucide-react-native";
import { useTranslations } from "@src/packages/i18n";
import { Tag } from "@louise/moon-ui";
import { EMBER } from "../../onboarding/theme";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { semanticColors, withAlpha } from "@louise/theme/tokens";

/**
 * Numeric score per evidence level (0-5 vials). Label & description live in
 * the i18n catalogue at learn.evidence_levels.<i18nKey>.
 */
export const EVIDENCE_LEVELS: Record<
  string,
  { score: number; i18nKey: string }
> = {
  "expert-opinion": { score: 1, i18nKey: "expert_opinion" },
  reference: { score: 2, i18nKey: "reference" },
  observational: { score: 2, i18nKey: "observational" },
  cohort: { score: 3, i18nKey: "cohort" },
  review: { score: 3, i18nKey: "review" },
  official: { score: 4, i18nKey: "official" },
  rct: { score: 4, i18nKey: "rct" },
  "meta-analysis": { score: 5, i18nKey: "meta_analysis" },
  guideline: { score: 5, i18nKey: "guideline" },
};

type Props = {
  level: string;
  onPress: () => void;
};

export default function EvidenceLevelChip({ level, onPress }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("EvidenceLevelChip");
  const info = EVIDENCE_LEVELS[level];
  if (!info) return null;

  const label = t(`learn.evidence_levels.${info.i18nKey}.label`);

  return (
    <Pressable
      onPress={() => {
        capturePress(
          pressLabel(label),
          `learn.evidence_levels.${info.i18nKey}.label`,
        );
        onPress();
      }}
      style={{ alignSelf: "flex-start" }}
    >
      <Tag
        variant="outline"
        label={label}
        leading={
          <View style={{ flexDirection: "row", alignItems: "center", gap: 1 }}>
            {Array.from({ length: 5 }).map((_, index) => (
              <FlaskConical
                key={index}
                size={9}
                color={
                  index < info.score
                    ? EMBER.colors.textMid
                    : withAlpha(semanticColors.text.secondary, 0.22)
                }
                strokeWidth={1.7}
              />
            ))}
          </View>
        }
        trailing={
          <Info size={10} color={EMBER.colors.textFaint} strokeWidth={1.5} />
        }
      />
    </Pressable>
  );
}
